import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ApiError } from "./api/client";
import { setQueryClientForAccountCaches } from "./lib/accountCaches";
import {
  handleNeedReload,
  handlePreloadError,
  markUpdateReady,
  startAppUpdateChecks,
} from "./lib/appUpdate";
import { initTheme } from "./lib/theme";
import { installSiteScale } from "./routes/marketing/siteScale";
import "./styles/tailwind.css";

initTheme();
installSiteScale();

// Deterministic client errors that will never change on a retry — a
// retry just doubles network/console noise (e.g. a 404 from a gated-off
// component, a 403/422 validation rejection).
const NON_RETRYABLE_STATUSES = new Set([400, 403, 404, 409, 422]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry transient failures once: a 401 retries after react-query
      // re-runs the queryFn, which re-mints the token via
      // `authHeader()` → Clerk `getToken()` (so a momentarily-stale /
      // just-rotated token recovers instead of hard-failing); 5xx,
      // timeouts and network errors are transient too. Deterministic 4xx
      // are NOT retried — see `NON_RETRYABLE_STATUSES`.
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;
        if (error instanceof ApiError && NON_RETRYABLE_STATUSES.has(error.status)) {
          return false;
        }
        return true;
      },
      // WS6 C.1 (refs #823) — TanStack v5's default `staleTime` is 0, so
      // any `useQuery` without its own `staleTime` is stale on arrival
      // and refetches on every component mount / route navigation. ~30
      // route-level query sites (incl. the 15-site `/config` fanout on
      // TeamPage + the setup pages) fall through to this default. A 30s
      // global default keeps team/config/cycles fresh-enough within a
      // navigation session and stops the refetch-on-every-mount storm;
      // it matches the 30s already set on the dedicated `api/*` hooks.
      // Mutations still invalidate explicitly, so freshness on write is
      // preserved.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// #2919 — sign-out and a change of account clear this client.
setQueryClientForAccountCaches(queryClient);

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Missing #root element");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);

// #2919 — a lazy chunk whose deployment is gone (Cloudflare Pages drops
// old assets) reloads the page once, or applies a waiting update.
window.addEventListener("vite:preloadError", handlePreloadError);

// PWA service worker (#966 / #997). PROD-only + dynamically imported so
// dev and vitest never resolve `virtual:pwa-register` or register a
// worker. #2919 — `prompt` mode (vite.config): a new build waits until
// `lib/appUpdate.ts` applies it at a safe moment (tab hidden, next page
// change, or the "Reload" strip), and the app checks for a new build
// every 60 s, on tab focus and when the browser comes back online.
// `onNeedReload` replaces the plugin's own reload: the new worker takes
// control of EVERY open tab, and a tab where the user types must wait.
if (import.meta.env.PROD) {
  void import("virtual:pwa-register").then(({ registerSW }) => {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh: () => markUpdateReady(updateSW),
      onNeedReload: handleNeedReload,
      onRegisteredSW: (swUrl, registration) => {
        startAppUpdateChecks(swUrl, registration);
      },
    });
  });
}
